
import { TRUSTED_ORIGINS } from "@card/common/constants";

type RawMessage = { origin: string, data: string }

export class WindowMessenger {
    private static MAX_MISSED_MESSAGES = 50

    private static prefixListeners: {
        [prefix: string]: {
            listener: (data: any, origin: string) => void
        }
    }

    // First In / First Out
    private static missedMessages: RawMessage[]

    private static initialized: boolean
    private static windowMessageListener: (messageEvent: MessageEvent) => void

    // loggers - publicly accessible
    // LOG defaults to debug, so if debug is specified, then log will also be emitted
    public static DEBUG: Function = function () { }
    public static LOG: Function = function () { }
    public static WARN: Function = console.warn.bind(console, WindowMessenger.logPrefix("WARN"))
    public static ERR: Function = console.error.bind(console, WindowMessenger.logPrefix("ERR"))
    public static logPrefix(type: string) {
        return `${type}[${window.location.origin + window.location.pathname}]`
    }

    // returns true if message was consumed
    private static processRawMessage(message: RawMessage): boolean {
        for (let messagePrefix in this.prefixListeners) {
            if (message.data.startsWith(messagePrefix)) {
                const { listener } = this.prefixListeners[messagePrefix]

                // parse message
                const messageJsonString = message.data.substring(messagePrefix.length)
                const messageData = JSON.parse(messageJsonString)

                // call listener
                listener(messageData, message.origin)
                return true
            }
        }

        return false
    }

    public static init() {
        if (this.initialized) {
            this.WARN(` WindowMessenger called to be initialized, but is already initialized`)
            return
        }

        this.initialized = true
        this.prefixListeners = {}
        this.missedMessages = []
        this.windowMessageListener = (messageEvent) => {
            if (typeof messageEvent.data !== "string") return

            const { data, origin } = messageEvent

            if (TRUSTED_ORIGINS.indexOf(origin) === -1) {
                // not trusted
                this.ERR(`Message received by WindowMessenger from unauthorized host "${origin}": "${data}"`)

                // TODO: Reenable and test with prod.
                // * Disabled checking of origin to save ourselves a ton of potential trouble in prod.
                // return
            }

            this.DEBUG(`Received message from ${origin}: ${data}`)

            const message: RawMessage = { origin, data }
            const messageReceived: boolean = this.processRawMessage(message)

            if (!messageReceived) {
                this.LOG(`Message missed from ${origin}: ${data}`)

                this.missedMessages.push(message)

                while (this.missedMessages.length > this.MAX_MISSED_MESSAGES) {
                    const droppedMessage = this.missedMessages.shift()
                    this.WARN(`Missed too many messages. Dropped message from ${droppedMessage.origin}: ${droppedMessage.data}`)
                }
            }
        }

        // add the master listener to current window
        window.addEventListener("message", this.windowMessageListener)

        this.DEBUG(`WindowMessenger initialized`)
    }

    private static onMessage<M>(prefix: string, listener: (data: M) => void, overwrite = false) {
        if (WindowMessenger.prefixListeners[prefix] != null) {
            if (overwrite) {
                this.WARN(`Overwriting existing listener with prefix: "${prefix}"`)
            } else {
                throw new ReferenceError(`Listener with prefix: "${prefix}" has already been added to WindowMessenger and overwrite is ${overwrite}`)
            }
        }

        this.prefixListeners[prefix] = { listener }
        this.DEBUG(`Listener with prefix: "${prefix}" added`)

        // apply missed messages
        const missedMessages = this.missedMessages
        if (missedMessages != null) {
            const missedAgain: RawMessage[] = []
            while (missedMessages.length > 0) {
                // get and remove the first missed message
                // First In / First Out
                const missed = missedMessages.shift()
                if (this.processRawMessage(missed)) {
                    this.DEBUG(`Processed missed message from ${missed.origin}: "${missed.data}"`)
                } else {
                    this.DEBUG(`Missed message again from ${missed.origin}: "${missed.data}"`)
                    missedAgain.push(missed)
                }
            }

            this.missedMessages = missedAgain
        }
    }

    // May never be needed
    public static removeReceivers() {
        this.WARN(`All message listeners (${Object.keys(this.prefixListeners)}) were removed from WindowMessenger.`)
        this.prefixListeners = {}
    }

    public static createPoster<T>(prefix: string, getTarget: () => Window): (data: T) => void {
        return (data) => {
            this.DEBUG(`Posted "${prefix}" message:`, data)
            getTarget().postMessage(prefix + JSON.stringify(data), '*')
        }
    }

    public static createReceiver<T>(prefix: string): (listener: (data: T) => void) => void {
        return (listener) => {
            this.DEBUG(`Adding listener for prefix: "${prefix}"`)
            this.onMessage<T>(prefix, listener, true)
        }
    }
}

// WindowMessenger.DEBUG = console.log.bind(console, '%c' + WindowMessenger.logPrefix("DEBUG"), 'color: purple')

WindowMessenger.init()

